import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'

import { SectionTitle } from '@apps-common/components/common/portal-title'
import { VertSpacerMd, VertSpacerXl } from '@apps-common/components/layout'
import { ColorPickerOverlay } from '@apps-common/components/modals/ColorPickerOverlay'
import { ThemeManagementForm, ThemeSelector } from '@apps-common/components/theme'
import { commonIcons } from '@apps-common/config/icons'
import { useAuth } from '@apps-common/contexts'
import { camelCaseToWords } from '@apps-common/helpers/camel-case-to-words'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import { useTheme } from '@apps-common/hooks/useTheme'
import { Authorization } from '@apps-common/lib'
import { useThemeStore } from '@apps-common/stores/useThemeStore'
import classNames from 'classnames'

export const ThemeManagementMenu = ({ roles, scopes, companiesData, createOrUpdateData }: any) => {
  const { user } = useAuth()
  const { themeMode, resetContextTheme, resetCompany } = useTheme()
  const { open, setOpen, editing, setModifiedPaletteValue, resetModifiedThemeToDefault } = useThemeStore()
  const themeTabStyles = useProjectHook('bg-brand border-on-brand text-on-brand', 'bg-mm-brand border-on-mm-brand text-on-mm-brand')
  const [isPickingColor, setIsPickingColor] = useState(false)
  const [currentKey, setCurrentKey] = useState<string>()
  const [currentColor, setCurrentColor] = useState<string>()

  const ThemeIcon = commonIcons.themeBrush
  const CloseIcon = commonIcons.close

  useEffect(() => {
    if (!editing) {
      setOpen(false)
      setIsPickingColor(false)
    }
  }, [editing])

  if (!user) return null

  return (
    <Authorization roles={roles} scopes={scopes}>
      {/* COLOR PICKER */}
      <motion.div
        initial={false}
        animate={{
          right: editing && open && isPickingColor ? '0px' : '-260px',
          transition: { ease: 'easeInOut', duration: 0.25, delay: open ? 0.2 : 0 }
        }}
        className='absolute top-0 z-[99] safe-area-top safe-area-right w-[226px] mr-[5px]'
      >
        <VertSpacerXl />
        <ColorPickerOverlay
          title={camelCaseToWords(currentKey || '', [themeMode]) || 'Color select'}
          defaultColor={currentColor}
          onChange={(color: string) => {
            if (!currentKey) return

            setCurrentColor(color)

            const [mode, field] = currentKey.split('.') as ['light' | 'dark', any]
            setModifiedPaletteValue(mode, field, color)
          }}
          onClose={() => setIsPickingColor(false)}
        />
      </motion.div>

      {/* MAIN PANEL */}
      <motion.div
        initial={false}
        animate={{
          right: editing && open && !isPickingColor ? '0px' : '-260px',
          transition: { ease: 'easeInOut', duration: 0.25, delay: open ? 0.2 : 0 }
        }}
        className='absolute top-0 z-[101] safe-area-top safe-area-right w-[260px] h-screen bg-surface-2 border-l-[1px] border-border drop-shadow-md flex flex-col justify-between'
      >
        <div className='fixed z-[2] bg-surface-2/10 text-secondary-content backdrop-blur-md h-[130px] w-full px-[10px] pb-[10px]'>
          <div className='flex items-center justify-between pt-[10px]'>
            <SectionTitle text='Theme management' classes='text-text' />
            <button type='button' aria-label='Theme management close' onClick={() => setOpen(false)} className='p-[10px] text-text'>
              <CloseIcon className='text-[1.65em]' />
            </button>
          </div>

          <VertSpacerMd />

          <div className='flex items-center justify-between'>
            <ThemeSelector />
            <button
              type='button'
              aria-label='Reset modified theme'
              onClick={() => {
                resetCompany()
                resetModifiedThemeToDefault()
                resetContextTheme()
              }}
              className='action-neutral gap-2 pl-3 pr-2'
            >
              Reset <commonIcons.refresh />
            </button>
          </div>

          <VertSpacerMd />
        </div>

        <div className='px-[10px]'>
          <ThemeManagementForm
            itemSelected={(key: string, color: string) => {
              setCurrentKey(key)
              setCurrentColor(color)
              setIsPickingColor(true)
            }}
            companiesData={companiesData}
            createOrUpdateData={createOrUpdateData}
          />
        </div>
      </motion.div>

      {/* FLOATING OPEN BUTTON */}
      <motion.div
        initial={false}
        animate={{
          right: !editing || open ? '-45px' : '0px',
          transition: { ease: 'easeInOut', delay: open ? 0 : 0.2 }
        }}
        className='absolute right-0 top-[45vh] z-[99]'
      >
        <button
          type='button'
          aria-label='Theme management open'
          onClick={() => setOpen(true)}
          className={classNames('border-l border-t border-b rounded-l-full', themeTabStyles)}
        >
          <div className='w-[30px] h-[40px] pl-[7.5px] flex items-center'>
            <ThemeIcon />
          </div>
        </button>
      </motion.div>
    </Authorization>
  )
}
